import { createFileRoute } from "@tanstack/react-router";
import { Layout } from "@/components/site/Layout";
import siteConfig from "@/config/site.json";
import { useT } from "@/lib/i18n";

export const Route = createFileRoute("/cookies")({
  component: CookiesPage,
  head: () => ({
    meta: [
      { title: `Cookie Policy — ${siteConfig.brand.name}` },
      {
        name: "description",
        content:
          "Information about the cookies used on the Bosque Suspendido website: types, purpose and how to manage them.",
      },
      { property: "og:title", content: `Cookie Policy — ${siteConfig.brand.name}` },
      {
        property: "og:description",
        content: "Cookies used on the Bosque Suspendido website.",
      },
      { property: "og:url", content: "https://bosque-suspendido.lovable.app/cookies" },
    ],
    links: [{ rel: "canonical", href: "https://bosque-suspendido.lovable.app/cookies" }],
  }),
});

function CookiesPage() {
  const { tk } = useT();
  const brand = siteConfig.brand.name;
  const email = siteConfig.contact.emails[0].address;

  return (
    <Layout>
      <article className="max-w-3xl mx-auto px-6 py-20">
        <p className="text-xs tracking-[0.3em] uppercase text-gold mb-4">
          {tk("legal.kicker", "Legal")}
        </p>
        <h1 className="text-4xl md:text-5xl font-serif mb-8">
          {tk("legal.cookies.title", "Cookie Policy")}
        </h1>
        <div className="gold-divider mb-10" />

        <div className="space-y-6 text-foreground/80 leading-relaxed">
          <p>
            {tk(
              "legal.cookies.intro.a",
              "This page explains what cookies are, which ones we use on the ",
            )}
            {brand}
            {tk(
              "legal.cookies.intro.b",
              " website and how you can manage them in your browser.",
            )}
          </p>

          <h2 className="font-serif text-2xl text-foreground mt-10">
            {tk("legal.cookies.s1.t", "1. What are cookies?")}
          </h2>
          <p>
            {tk(
              "legal.cookies.s1.d",
              "Cookies are small text files that websites store on your device to remember information about your visit, such as language preferences or other options.",
            )}
          </p>

          <h2 className="font-serif text-2xl text-foreground mt-10">
            {tk("legal.cookies.s2.t", "2. Types of cookies we use")}
          </h2>
          <ul className="list-disc pl-6 space-y-2">
            <li>
              <strong className="text-foreground">
                {tk("legal.cookies.s2.tech.t", "Technical cookies:")}
              </strong>{" "}
              {tk(
                "legal.cookies.s2.tech.d",
                "required for the site to function correctly.",
              )}
            </li>
            <li>
              <strong className="text-foreground">
                {tk("legal.cookies.s2.pref.t", "Preference cookies:")}
              </strong>{" "}
              {tk(
                "legal.cookies.s2.pref.d",
                "remember choices such as the selected language.",
              )}
            </li>
            <li>
              <strong className="text-foreground">
                {tk("legal.cookies.s2.an.t", "Analytics cookies:")}
              </strong>{" "}
              {tk(
                "legal.cookies.s2.an.d",
                "let us measure site usage in aggregate and anonymous form to improve the experience.",
              )}
            </li>
          </ul>

          <h2 className="font-serif text-2xl text-foreground mt-10">
            {tk("legal.cookies.s3.t", "3. Managing cookies")}
          </h2>
          <p>
            {tk(
              "legal.cookies.s3.d",
              "You can accept, reject or delete cookies at any time from your browser settings. Disabling some cookies may affect site functionality.",
            )}
          </p>
          <div className="pt-4">
            <CookiePreferencesButton />
          </div>

          <h2 className="font-serif text-2xl text-foreground mt-10">
            {tk("legal.cookies.s4.t", "4. More information")}
          </h2>
          <p>
            {tk(
              "legal.cookies.s4.a",
              "For any questions about this policy, contact us at ",
            )}
            <a className="text-gold hover:underline" href={`mailto:${email}`}>
              {email}
            </a>
            .
          </p>
        </div>
      </article>
    </Layout>
  );
}

function CookiePreferencesButton() {
  const { tk } = useT();
  return (
    <button
      type="button"
      onClick={() => window.pulso33Consent?.open()}
      className="inline-flex items-center gap-2 rounded-full border border-gold/40 px-5 py-2.5 text-sm tracking-[0.2em] uppercase text-gold hover:bg-gold hover:text-primary-foreground transition-colors font-semibold"
    >
      {tk("cookies.preferencesButton", "Manage cookie preferences")}
    </button>
  );
}
